// Base implementation copied over from https://github.com/adobe/aem-react-core-wcm-components-spa/tree/master/src/container/accordion/v1

import React from 'react';
import {
  AccordionV1Properties,
  CoreContainerState,
  withAuthorPanelSwitch,
  withStandardBaseCssClass,
} from '@adobe/aem-core-components-react-spa';
import {
  ComponentMapping,
  Container,
} from '@adobe/aem-react-editable-components';
import styled, { css } from 'styled-components';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { adobeTargetTriggerView } from 'shared/hooks/useTriggerView/useTriggerView.hook';
import { AccordionEditConfig } from './AccordionEditConfig';
import { AccordionDisplayItem } from './components';

export interface AccordionV1State extends CoreContainerState {
  expandedItems: string[];
}
interface AccordionItemProps {
  'cq:panelTitle': string;
  categories: string[];
  id?: string;
}

interface AccordionProperties extends AccordionV1Properties {
  filterKey: string;
  regionId: string;
}

const AccordionWrapper = styled.div`
  ${({ theme: { media } }) => css`
    ${media.up('big')} {
      scroll-behavior: smooth;
      scroll-margin-top: 80px;
    }
  `}
`;

class AccordionV1Impl extends Container<AccordionProperties, AccordionV1State> {
  constructor(props: AccordionProperties) {
    super(props);

    this.state = {
      componentMapping: this.props.componentMapping || ComponentMapping,
      expandedItems: this.props.expandedItems,
    };

    this.handleAccordionNavClick = this.handleAccordionNavClick.bind(this);
    this.handleHashExpansion = this.handleHashExpansion.bind(this);
  }

  componentDidMount() {
    this.handleHashExpansion();
    window.addEventListener('hashchange', this.handleHashExpansion);
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.handleHashExpansion);
  }

  componentDidUpdate(
    prevProps: Readonly<AccordionV1Properties>,
    _prevState: Readonly<AccordionV1State>,
  ): void {
    if (
      this.props.activeIndexFromAuthorPanel !== undefined &&
      prevProps.activeIndexFromAuthorPanel !==
        this.props.activeIndexFromAuthorPanel
    ) {
      this.setState({
        expandedItems: [
          this.props.cqItemsOrder[this.props.activeIndexFromAuthorPanel],
        ],
      });
    }
  }

  handleHashExpansion() {
    const hash = window.location.hash.substring(1);

    const expandedItemKey = Object.keys(this.props.cqItems).find(
      (key) => (this.props.cqItems[key] as AccordionItemProps).id === hash,
    );

    if (expandedItemKey) {
      this.setState({
        expandedItems: [expandedItemKey],
      });
    }
  }

  clearHash() {
    window.history.pushState(
      '',
      '',
      window.location.pathname + window.location.search,
    );
  }

  handleAccordionNavClick(itemKey: string, itemTitle: string) {
    const isActive = this.state.expandedItems.indexOf(itemKey) > -1;
    if (isActive) {
      adobeTargetTriggerView(itemKey);
    }
    const isSingleExpansion = this.props.singleExpansion;

    trackCustomLink(itemTitle);

    let expandedItems = this.state.expandedItems;

    if (isSingleExpansion) {
      expandedItems = isActive ? [] : [itemKey];
    } else {
      if (isActive) {
        const index = this.state.expandedItems.indexOf(itemKey);
        expandedItems.splice(index, 1);
      } else {
        expandedItems.push(itemKey);
      }
    }

    this.setState({
      expandedItems: expandedItems,
    });

    if (!expandedItems.length) {
      this.clearHash();
    }
  }

  handleItemChange() {
    // Dont do anything here. The way AEM works it needs multiple accordions so that we can move them around.
    // Future Work: Add an onClick handler in Pattern lib, make onChange optional.
  }

  isItemExpanded(key: string) {
    return this.state.expandedItems.indexOf(key) > -1;
  }

  get accordionContainerProps() {
    const attrs = this.containerProps;
    attrs['className'] = attrs.className + ' ' + this.props.baseCssClass;
    attrs['data-cmp-is'] = 'accordion';
    attrs['data-panelcontainer'] = 'accordion';
    return attrs;
  }

  renderHeadingButton(
    key: string,
    item: AccordionItemProps,
    buttonCssClass: string,
  ) {
    return (
      <div
        className={buttonCssClass}
        key={key}
        onClick={() => {
          this.handleAccordionNavClick(key, item['cq:panelTitle']);
        }}
      >
        <span className={this.props.baseCssClass + '__title'}>
          {item['cq:panelTitle']}
        </span>
      </div>
    );
  }

  get accordionContent() {
    return this.props.cqItemsOrder.map((key) => {
      const { cqItems, cqItemsOrder, filterKey, isInEditor } = this.props;

      const isExpanded = this.isItemExpanded(key);
      const indexToShow = cqItemsOrder.indexOf(key);

      const item = cqItems[key];
      const {
        'cq:panelTitle': title,
        categories,
        id: accordionItemId,
      } = item as AccordionItemProps;

      return (
        <AccordionWrapper id={accordionItemId ?? key} key={key}>
          <AccordionDisplayItem
            categories={categories}
            id={key}
            isInEditor={isInEditor}
            index={indexToShow}
            isOpen={isExpanded}
            isSingleExpansion={this.props.singleExpansion}
            onClick={() => this.handleAccordionNavClick(key, title)}
            queryParamToUse={filterKey}
            title={title}
          >
            {this.childComponents}
          </AccordionDisplayItem>
        </AccordionWrapper>
      );
    });
  }

  render() {
    const isEmpty = AccordionEditConfig.isEmpty(this.props);
    const { regionId = 'accordion' } = this.props;

    return (
      <div {...this.accordionContainerProps}>
        <LinkRegion regionId={regionId}>
          {!isEmpty && this.accordionContent}
          {this.placeholderComponent}
        </LinkRegion>
      </div>
    );
  }
}

export default withStandardBaseCssClass(
  withAuthorPanelSwitch(AccordionV1Impl),
  'cmp-accordion',
);
